/**
 * Privacy Policy Page
 * For isupplyai.com
 */

import React from 'react';
import { Shield, Mail, Database, Lock, Globe, UserCheck } from 'lucide-react';

const PrivacyPolicy = () => {
  const lastUpdated = "December 23, 2025";
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white">
      {/* Header */}
      <div className="bg-gray-900/80 border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-10 h-10 text-purple-500" />
            <h1 className="text-3xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-gray-400">Last updated: {lastUpdated}</p>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-invert prose-lg max-w-none">
          
          {/* Introduction */}
          <section className="mb-12">
            <p className="text-gray-300 text-lg leading-relaxed">
              iSupplyAI (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you use our AI-powered content marketing platform at isupplyai.com (the &quot;Service&quot;).
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-6 h-6 text-blue-400" />
              <h2 className="text-2xl font-bold text-white m-0">Information We Collect</h2>
            </div>
            
            <h3 className="text-xl font-semibold text-gray-200 mt-6">Information You Provide</h3>
            <ul className="text-gray-300 space-y-2">
              <li><strong>Account Information:</strong> Email address, name, and password when you register</li>
              <li><strong>Profile Data:</strong> Company name, industry, and brand voice preferences</li>
              <li><strong>Content Data:</strong> Topics, content preferences, and generated content you create</li>
              <li><strong>Payment Information:</strong> Billing details processed securely through our payment providers</li>
              <li><strong>Connected Accounts:</strong> OAuth tokens when you connect LinkedIn, Medium, or other platforms</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-200 mt-6">Information Collected Automatically</h3>
            <ul className="text-gray-300 space-y-2">
              <li><strong>Usage Data:</strong> Features used, content generated, and interaction patterns</li>
              <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers</li>
              <li><strong>Log Data:</strong> IP address, access times, and pages viewed</li>
              <li><strong>Cookies:</strong> Session cookies for authentication and preferences</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <UserCheck className="w-6 h-6 text-green-400" />
              <h2 className="text-2xl font-bold text-white m-0">How We Use Your Information</h2>
            </div>
            <ul className="text-gray-300 space-y-2">
              <li>Provide, maintain, and improve our AI content generation services</li>
              <li>Process your transactions and manage your subscription</li>
              <li>Generate personalized content based on your brand voice settings</li>
              <li>Distribute content to connected social media platforms on your behalf</li>
              <li>Send service updates, security alerts, and support messages</li>
              <li>Analyze usage patterns to enhance our AI models and features</li>
              <li>Detect and prevent fraud, abuse, or security incidents</li>
            </ul>
          </section>

          {/* AI and Data Processing */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Globe className="w-6 h-6 text-purple-400" />
              <h2 className="text-2xl font-bold text-white m-0">AI and Data Processing</h2>
            </div>
            <p className="text-gray-300">
              Our Service uses artificial intelligence and machine learning technologies, including:
            </p>
            <ul className="text-gray-300 space-y-2">
              <li><strong>OpenAI:</strong> For content generation, analysis, and natural language processing</li>
              <li><strong>Third-Party AI Services:</strong> For image generation and content optimization</li>
            </ul>
            <p className="text-gray-300 mt-4">
              Content you input may be processed by these AI services to generate outputs. We do not use your 
              content to train AI models. Your brand voice settings and preferences are stored securely and 
              used only to personalize your experience.
            </p>
          </section>

          {/* Data Sharing */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-6 h-6 text-yellow-400" />
              <h2 className="text-2xl font-bold text-white m-0">Data Sharing and Disclosure</h2>
            </div>
            <p className="text-gray-300">We may share your information with:</p>
            <ul className="text-gray-300 space-y-2">
              <li><strong>Service Providers:</strong> Third parties that help us operate (hosting, payment processing, analytics)</li>
              <li><strong>Connected Platforms:</strong> LinkedIn, Medium, Twitter, Facebook, Instagram when you authorize posting</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
            <p className="text-gray-300 mt-4">
              <strong>We do not sell your personal information to third parties.</strong>
            </p>
          </section>

          {/* Data Security */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white">Data Security</h2>
            <p className="text-gray-300">
              We implement industry-standard security measures to protect your data, including:
            </p>
            <ul className="text-gray-300 space-y-2">
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Secure password hashing</li>
              <li>Regular security audits and monitoring</li>
              <li>Access controls and authentication requirements</li>
            </ul>
            <p className="text-gray-300 mt-4">
              While we strive to protect your information, no method of transmission over the Internet 
              is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white">Your Rights and Choices</h2>
            <p className="text-gray-300">Depending on your location, you may have the right to:</p>
            <ul className="text-gray-300 space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Portability:</strong> Receive your data in a portable format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Disconnect:</strong> Revoke access to connected social platforms at any time</li>
            </ul>
            <p className="text-gray-300 mt-4">
              To exercise these rights, contact us at privacy@isupplyai.com
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white">Data Retention</h2>
            <p className="text-gray-300">
              We retain your information for as long as your account is active or as needed to provide services. 
              You may request deletion of your account at any time. Some data may be retained for legal 
              compliance, dispute resolution, or fraud prevention purposes.
            </p>
          </section>

          {/* Cookies */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white">Cookies and Tracking</h2>
            <p className="text-gray-300">
              We use cookies and similar technologies for:
            </p>
            <ul className="text-gray-300 space-y-2">
              <li><strong>Essential Cookies:</strong> Required for authentication and security</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              <li><strong>Analytics Cookies:</strong> Understand how you use our Service</li>
            </ul>
            <p className="text-gray-300 mt-4">
              You can control cookies through your browser settings, though some features may not function properly without them.
            </p>
          </section>

          {/* Children */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white">Children&apos;s Privacy</h2>
            <p className="text-gray-300">
              Our Service is not intended for children under 16 years of age. We do not knowingly collect 
              personal information from children. If we become aware that we have collected data from a child, 
              we will take steps to delete it promptly.
            </p>
          </section>

          {/* International */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white">International Data Transfers</h2>
            <p className="text-gray-300">
              Your information may be transferred to and processed in countries other than your own. 
              We ensure appropriate safeguards are in place to protect your data in accordance with 
              applicable laws, including GDPR and CCPA requirements.
            </p>
          </section>

          {/* Changes */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white">Changes to This Policy</h2>
            <p className="text-gray-300">
              We may update this Privacy Policy from time to time. We will notify you of any material 
              changes by posting the new policy on this page and updating the &quot;Last updated&quot; date. 
              Your continued use of the Service after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-12 bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-purple-400" />
              <h2 className="text-2xl font-bold text-white m-0">Contact Us</h2>
            </div>
            <p className="text-gray-300">
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="mt-4 space-y-2 text-gray-300">
              <p><strong>Email:</strong> privacy@isupplyai.com</p>
              <p><strong>Website:</strong> isupplyai.com</p>
            </div>
          </section>

        </div>
        
        {/* Back to Home */}
        <div className="text-center mt-12">
          <a 
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
